
import { useEffect, useState } from 'react';

export function TodayChart() {
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<'power' | 'energy'>('power');

  useEffect(() => {
    // Generar datos simulados para el día actual
    const generateTodayData = () => {
      const data = [];
      const now = new Date();
      const currentHour = now.getHours();
      
      for (let hour = 0; hour < 24; hour++) {
        const isDaytime = hour >= 6 && hour <= 18;
        const isCurrentOrPast = hour <= currentHour;
        
        // Curva de producción solar realista
        let pvPower = 0;
        if (isDaytime && isCurrentOrPast) {
          const solarCurve = Math.sin(((hour - 6) / 12) * Math.PI);
          pvPower = Math.floor(solarCurve * (2000 + Math.random() * 500));
        }
        
        // Curva de consumo realista
        let loadPower = 800; // Consumo base
        if (hour >= 6 && hour <= 9) loadPower += 400; // Mañana
        if (hour >= 12 && hour <= 14) loadPower += 200; // Mediodía
        if (hour >= 18 && hour <= 22) loadPower += 600; // Noche
        loadPower += Math.random() * 200;
        
        if (!isCurrentOrPast) {
          pvPower = 0;
          loadPower = 0;
        }
        
        const gridPower = loadPower - pvPower;
        const selfConsumption = Math.min(pvPower, loadPower);
        
        data.push({
          hour: `${hour.toString().padStart(2, '0')}:00`,
          pvPower: Math.floor(pvPower),
          loadPower: Math.floor(loadPower),
          gridPower: Math.floor(gridPower),
          selfConsumption: Math.floor(selfConsumption),
          pvEnergy: Math.floor(pvPower / 1000), // kWh
          loadEnergy: Math.floor(loadPower / 1000),
          gridEnergy: Math.floor(gridPower / 1000)
        });
      }
      
      setChartData(data);
    };

    generateTodayData();
    return () => {};
  }, []);

  const maxValue = Math.max(...chartData.map(d => 
    selectedMetric === 'power' 
      ? Math.max(d.pvPower, d.loadPower)
      : Math.max(d.pvEnergy, d.loadEnergy)
  ));

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 h-96">
      {/* Controles */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setSelectedMetric('power')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedMetric === 'power'
                ? 'bg-cyan-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Potencia (W)
          </button>
          <button
            onClick={() => setSelectedMetric('energy')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedMetric === 'energy'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Energía (kWh)
          </button>
        </div>
        
        <div className="flex items-center space-x-4 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
            <span>Producción</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span>Consumo</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-400 rounded-full"></div>
            <span>Autoconsumo</span>
          </div>
        </div>
      </div>

      {/* Gráfico */}
      <div className="relative h-64 overflow-x-auto">
        <div className="flex items-end justify-between h-full min-w-full space-x-1">
          {chartData.map((data, index) => {
            const pvValue = selectedMetric === 'power' ? data.pvPower : data.pvEnergy;
            const loadValue = selectedMetric === 'power' ? data.loadPower : data.loadEnergy;
            const selfValue = selectedMetric === 'power' ? data.selfConsumption : Math.min(data.pvEnergy, data.loadEnergy);
            
            const pvHeight = maxValue > 0 ? (pvValue / maxValue) * 100 : 0;
            const loadHeight = maxValue > 0 ? (loadValue / maxValue) * 100 : 0;
            const selfHeight = maxValue > 0 ? (selfValue / maxValue) * 100 : 0;
            
            return (
              <div key={index} className="flex flex-col items-center space-y-1 group cursor-pointer">
                {/* Barras */}
                <div className="relative flex items-end space-x-1 h-48">
                  {/* Producción Solar */}
                  <div
                    className="w-4 bg-gradient-to-t from-yellow-400 to-yellow-300 rounded-t-sm transition-all duration-300 group-hover:from-yellow-500 group-hover:to-yellow-400"
                    style={{ height: `${pvHeight}%` }}
                    title={`Producción: ${pvValue}${selectedMetric === 'power' ? 'W' : 'kWh'}`}
                  ></div>
                  
                  {/* Consumo */}
                  <div
                    className="w-4 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-sm transition-all duration-300 group-hover:from-blue-600 group-hover:to-blue-500"
                    style={{ height: `${loadHeight}%` }}
                    title={`Consumo: ${loadValue}${selectedMetric === 'power' ? 'W' : 'kWh'}`}
                  ></div>
                  
                  {/* Autoconsumo */}
                  <div
                    className="w-4 bg-gradient-to-t from-green-400 to-green-300 rounded-t-sm transition-all duration-300 group-hover:from-green-500 group-hover:to-green-400"
                    style={{ height: `${selfHeight}%` }}
                    title={`Autoconsumo: ${selfValue}${selectedMetric === 'power' ? 'W' : 'kWh'}`}
                  ></div>
                </div>
                
                {/* Hora */}
                <span className="text-xs text-gray-500 transform -rotate-45 origin-center whitespace-nowrap">
                  {data.hour}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Resumen del día */}
      <div className="mt-4 grid grid-cols-3 gap-4 text-center">
        <div className="bg-yellow-50 rounded-lg p-3">
          <div className="text-lg font-bold text-yellow-700">
            {chartData.reduce((sum, d) => sum + (selectedMetric === 'power' ? d.pvPower : d.pvEnergy), 0).toLocaleString()}
          </div>
          <div className="text-xs text-yellow-600">
            Total Producido {selectedMetric === 'power' ? '(W)' : '(kWh)'}
          </div>
        </div>
        
        <div className="bg-blue-50 rounded-lg p-3">
          <div className="text-lg font-bold text-blue-700">
            {chartData.reduce((sum, d) => sum + (selectedMetric === 'power' ? d.loadPower : d.loadEnergy), 0).toLocaleString()}
          </div>
          <div className="text-xs text-blue-600">
            Total Consumido {selectedMetric === 'power' ? '(W)' : '(kWh)'}
          </div>
        </div>
        
        <div className="bg-green-50 rounded-lg p-3">
          <div className="text-lg font-bold text-green-700">
            {chartData.reduce((sum, d) => sum + (selectedMetric === 'power' ? d.selfConsumption : Math.min(d.pvEnergy, d.loadEnergy)), 0).toLocaleString()}
          </div>
          <div className="text-xs text-green-600">
            Total Autoconsumo {selectedMetric === 'power' ? '(W)' : '(kWh)'}
          </div>
        </div>
      </div>
    </div>
  );
}
